import React, { useState } from "react";
import { FaInfoCircle } from "react-icons/fa";
import { Tooltip } from "../../assets/icons/Tooltip";
import SubscriptionMarkDone from "../../assets/icons/SubscriptionMarkDone";
import { formatMoney } from "../../utils";
import { Button } from "../common";
import { useTranslation } from "react-i18next";
import FreeIcon from "../../assets/icons/Sparkle";
import BasicIcon from "../../assets/icons/Basic";
import PremiumIcon from "../../assets/icons/Premium";
import NewCheckIcon from "../../assets/icons/NewCheckIcon";
import OrangeCheckIcon from "../../assets/icons/OrangeCheckIcon";
import GreenCheckIcon from "../../assets/icons/GreenCheckIcon";
import { createSubscriptionPaymentLink } from "../../backend-services";

interface PricingPlanProps {
  title: string;
  duration: string;
  price: number;
  subPrice?: number;
  features: { name: string; tooltip?: string }[];
  buttonText: string;
  index: number;
  plan: string;
  discount?: string;
}

const PricingPlan: React.FC<PricingPlanProps> = ({
  title,
  price,
  features,
  buttonText,
  index,
  plan,
  duration,
  discount,
  subPrice,
}) => {
  const callbackUrl = `${window.location.origin}/settings?tab=subscription&status=processing`;
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handlePaymentSucess = async (plan: string) => {
    setIsLoading(true);
    try {
      const data = await createSubscriptionPaymentLink({
        paymentProvider: "paystack",
        plan: plan,
        callback_url: callbackUrl,
      });
      const { authorization_url } = data;
      window.location = authorization_url;
    } catch (error: any) {
      throw new Error(error);
    }
    setIsLoading(false);
  };
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const { t } = useTranslation("subscription");

  // Map the title to its corresponding icon
  const getPlanIcon = (title: string) => {
    switch (title) {
      case "Free plan":
        return <FreeIcon />;
      case "Essential plan":
        return <BasicIcon />;
      case "Premium plan":
        return <PremiumIcon />;
      default:
        return null; // Fallback if no match
    }
  };

  const getCheckIcon = (title: string) => {
    switch (title) {
      case "Free plan":
        return <NewCheckIcon />;
      case "Essential plan":
        return <OrangeCheckIcon />;
      case "Premium plan":
        return <GreenCheckIcon />;
      default:
        return null; // Fallback if no match
    }
  };
  const [showAll, setShowAll] = useState(false);

  const visibleFeatures = showAll ? features : features.slice(0, 5);

  return (
    <div
      className={`pt-[22px] md:pb-20 pb-10 md:w-[407px] w-full flex flex-col  gap-4  md:justify-between md:items-center relative 
    ${index === 0 ? "md:rounded-l-3xl bg-[#F9FAFB] rounded-lg md:rounded-none border-r border-y" : index === 1 ? " md:rounded-r-3xl rounded-lg md:rounded-none border bg-white" : " rounded-lg md:rounded-none bg-gray-50"}`}
    >
      <div className="w-full ">
        <div className="flex flex-col justify-start items-start space-y-[34px] w-full border-b md:border-none pb-4 px-[18px]">
          <div className="w-full flex flex-col justify-start items-start ">
            <div className="flex items-center justify-between w-full">
              {getPlanIcon(title)}
              <p
                className={`text-[#084784]  px-2 py-1 rounded-md text-md ${index === 0 ? "bg-[#BBDDFC]" : "bg-white"}`}
              >
                {index === 0 ? "Popular" : ""}
              </p>
            </div>
            <p className="text-[#2B2B2B] text-[20px]  mt-4">{title} </p>
          </div>

          <p className=" text-[#979797] lg:text-[16px] flex flex-col justify-start items-start md:h-[80px]">
            <span
              style={{ fontWeight: 600 }}
              className="lg:text-[25px] text-[#000]"
            >
              {formatMoney(price)}
            </span>
          </p>

          <div className="text-[14px] w-full md:flex justify-center md:text-[16px] hidden ">
            <button
              onClick={() => handlePaymentSucess(plan)}
              className={`w-full py-3 rounded-md transition-colors duration-300 ${"bg-[#FF6702] hover:bg-orange-600 text-white"}`}
            >
              {isLoading ? "Loading..." : buttonText}
            </button>
          </div>
        </div>

        <ul className="space-y-[19px] mt-[20px] md:mt-[47px] px-[18px] ">
          {visibleFeatures.map((feature, index) => (
            <li
              key={index}
              className=" flex items-center md:gap-[15px] gap-[14px]"
            >
              <NewCheckIcon />
              <div className="flex items-center gap-1 text-[#2B2B2B]">
                <p className="text-[13px] xl:text-[16px] leading-[19.2px] font-normal text-[#2B2B2B]">
                  {feature.name}
                </p>
              </div>
            </li>
          ))}
        </ul>

        <div className="items-start flex px-[18px] justify-start">
          {features.length > 5 && (
            <a
              href="https://esemie.io/pricings/"
              target="_blank"
              className="text-[#084784] font-semibold mt-4"
            >
              {showAll ? "Show Less" : "and lots more"}
            </a>
          )}
        </div>

        <div className="text-[14px] w-full flex md:hidden justify-center md:text-[16px] px-4 mt-6 ">
          <button
            onClick={() => handlePaymentSucess(plan)}
            disabled={index === 0}
            className={`w-full py-3 rounded-md transition-colors duration-300 ${
              index === 1
                ? "bg-[#FF6702] hover:bg-orange-600 text-white"
                : "bg-gray-100 mt-2 hover:bg-gray-200 text-black"
            }`}
          >
            {isLoading ? "Loading..." : buttonText}
          </button>
        </div>
      </div>

      {index === 1 && (
        <div className="absolute left-[-2.5em] bottom-[-3.5em] z-10">
          <div className="flex items-center justify-center">
            <svg
              width="18"
              height="15"
              viewBox="0 0 18 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M9 0L17.6603 15H0.339746L9 0Z" fill="#FFF4C6" />
            </svg>
          </div>
          {/* <div className="h-[59px] w-[394px] bg-[#FFF4C6] rounded-[12px] text-[#5B5B5B] font-medium leading-[19.2px] flex justify-center items-center">
            {t("subscription.mostPopular")}
          </div> */}
        </div>
      )}
    </div>
  );
};

export default PricingPlan;
